import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, DollarSign, TrendingUp, CheckCircle, XCircle, Clock, 
  Search, Filter, Eye, Check, X, CreditCard, Calendar, Mail,
  Phone, Globe, Building, AlertCircle, Trash2, Send
} from 'lucide-react';
import api from '../../api/api';
import { useToast } from '../ui/Toast';
import { useConfirmModal } from '../ui/ConfirmModal';

const AffiliateManagement = () => {
  const { showToast } = useToast();
  const { showPrompt, ConfirmModal } = useConfirmModal();
  const [activeTab, setActiveTab] = useState('affiliates');
  const [affiliates, setAffiliates] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    console.log('[AffiliateManagement] Fetching affiliate data...');
    try {
      const [affiliatesRes, payoutsRes, commissionsRes] = await Promise.all([
        api.get('/affiliates/admin/list'),
        api.get('/affiliates/admin/payouts'),
        api.get('/affiliates/admin/commissions')
      ]);
      
      console.log('[AffiliateManagement] API Response:', affiliatesRes.data);
      console.log('[AffiliateManagement] Affiliates count:', affiliatesRes.data.data?.affiliates?.length);
      console.log('[AffiliateManagement] Stats:', affiliatesRes.data.data?.stats);
      
      // Backend wraps response in { success: true, data: {...} }
      setAffiliates(affiliatesRes.data.data?.affiliates || affiliatesRes.data.affiliates || []);
      setStats(affiliatesRes.data.data?.stats || affiliatesRes.data.stats || {});
      setPayouts(payoutsRes.data.data?.payouts || payoutsRes.data.payouts || []);
      setCommissions(commissionsRes.data.data?.commissions || commissionsRes.data.commissions || []);
      setCommissionSummary(commissionsRes.data.data?.summary || commissionsRes.data.summary || null);
      
      console.log('[AffiliateManagement] Data loaded successfully');
    } catch (error) {
      console.error('[AffiliateManagement] Error fetching data:', error);
      console.error('[AffiliateManagement] Error status:', error.response?.status);
      console.error('[AffiliateManagement] Error message:', error.response?.data?.error || error.message);
      
      // Only redirect on 401 - don't redirect on network errors or other issues
      if (error.response?.status === 401) {
        console.error('[AffiliateManagement] 401 Unauthorized - logging out admin');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      } else {
        // For other errors, show error message but keep the page loaded
        console.warn('[AffiliateManagement] Non-401 error, showing empty state');
        setAffiliates([]);
        setStats({});
        setPayouts([]);
        
        // Show error toast if available
        if (showToast) {
          showToast(
            'Failed to load affiliate data: ' + (error.response?.data?.error || error.message || 'Unknown error'),
            'error'
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (affiliateId) => {
    try {
      await api.post('/affiliates/admin/approve', { affiliateId });
      fetchData();
      setShowDetailModal(false);
      showToast('Affiliate approved successfully!', 'success');
    } catch (error) {
      showToast('Error approving affiliate: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleReject = async (affiliateId) => {
    const reason = await showPrompt({
      title: 'Reject Affiliate',
      message: 'Please provide a reason for rejecting this affiliate application:',
      inputLabel: 'Rejection Reason',
      inputPlaceholder: 'Enter reason...',
      confirmText: 'Reject',
      variant: 'danger',
      onConfirm: async (inputValue) => {
        try {
          await api.post('/affiliates/admin/reject', { affiliateId, reason: inputValue });
          fetchData();
          setShowDetailModal(false);
          showToast('Affiliate rejected successfully!', 'success');
        } catch (error) {
          showToast('Error rejecting affiliate: ' + (error.response?.data?.error || error.message), 'error');
        }
      }
    });
  };

  const handleDelete = async (affiliateId, affiliateName) => {
    await showPrompt({
      title: 'Delete Affiliate',
      message: `Are you sure you want to permanently delete ${affiliateName}? This will also remove all their referrals and payout records. This action cannot be undone.`,
      inputLabel: 'Type "DELETE" to confirm',
      inputPlaceholder: 'DELETE',
      confirmText: 'Delete Permanently',
      variant: 'danger',
      onConfirm: async (inputValue) => {
        if (inputValue !== 'DELETE') {
          showToast('Please type DELETE to confirm', 'error');
          return;
        }
        try {
          await api.post('/affiliates/admin/delete', { affiliateId });
          fetchData();
          setShowDetailModal(false);
          showToast('Affiliate deleted successfully!', 'success');
        } catch (error) {
          showToast('Error deleting affiliate: ' + (error.response?.data?.error || error.message), 'error');
        }
      }
    });
  };

  const handleResendAccess = async (affiliateId, affiliateName, affiliateEmail) => {
    try {
      const response = await api.post('/affiliates/admin/resend-access', { affiliateId });
      if (response.data.success) {
        showToast(`New access link sent to ${affiliateEmail}!`, 'success');
        fetchData();
      }
    } catch (error) {
      showToast('Error resending access link: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleProcessPayout = async () => {
    await showPrompt({
      title: 'Process Payout',
      message: 'Enter the transaction ID for this payout:',
      inputLabel: 'Transaction ID',
      inputPlaceholder: 'e.g., TXN123456789',
      confirmText: 'Process Payout',
      variant: 'success',
      onConfirm: async (inputValue) => {
        try {
          await api.post('/affiliates/admin/process-payout', { 
            payoutId: selectedPayout.id,
            transactionId: inputValue,
            note: 'Processed by admin'
          });
          fetchData();
          setShowPayoutModal(false);
          showToast('Payout processed successfully!', 'success');
        } catch (error) {
          showToast('Error processing payout: ' + (error.response?.data?.error || error.message), 'error');
        }
      }
    });
  };

  const filteredAffiliates = affiliates.filter(aff => {
    const matchesSearch = aff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          aff.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || aff.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white"
          whileHover={{ scale: 1.02 }}
        >
          <Users size={32} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats.total || 0}</p>
          <p className="text-sm opacity-90">Total Affiliates</p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white"
          whileHover={{ scale: 1.02 }}
        >
          <CheckCircle size={32} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats.active || 0}</p>
          <p className="text-sm opacity-90">Active Affiliates</p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white"
          whileHover={{ scale: 1.02 }}
        >
          <Clock size={32} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats.pending || 0}</p>
          <p className="text-sm opacity-90">Pending Approval</p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white"
          whileHover={{ scale: 1.02 }}
        >
          <XCircle size={32} className="mb-2 opacity-80" />
          <p className="text-3xl font-bold">{stats.rejected || 0}</p>
          <p className="text-sm opacity-90">Rejected</p>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`py-4 font-semibold ${
                activeTab === 'affiliates'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              Affiliates
            </button>
            <button
              onClick={() => setActiveTab('payouts')}
              className={`py-4 font-semibold ${
                activeTab === 'payouts'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              Payouts
            </button>
            <button
              onClick={() => setActiveTab('commissions')}
              className={`py-4 font-semibold ${
                activeTab === 'commissions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              Monthly Commissions
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'affiliates' && (
            <div className="space-y-4">
              {/* Search and Filter */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search affiliates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Affiliates Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Code</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Referrals</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Earnings</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAffiliates.map((aff) => (
                      <tr key={aff.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-black">{aff.name}</p>
                          {aff.company && <p className="text-xs text-gray-500">{aff.company}</p>}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{aff.email}</td>
                        <td className="py-3 px-4">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                            {aff.referralCode}
                          </code>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(aff.status)}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {aff.totalReferrals || 0} ({aff.activeReferrals || 0} active)
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-green-600">
                          ₹{(aff?.totalEarnings || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedAffiliate(aff);
                                setShowDetailModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm p-1"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            {aff.status === 'active' && (
                              <button
                                onClick={() => handleResendAccess(aff.id, aff.name, aff.email)}
                                className="text-green-600 hover:text-green-800 font-medium text-sm p-1"
                                title="Resend Access Link"
                              >
                                <Send size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(aff.id, aff.name)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm p-1"
                              title="Delete Affiliate"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-4">
              {/* Payout Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total Payouts</p>
                  <p className="text-2xl font-bold text-black">{payouts.length}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {payouts.filter(p => p.status === 'pending').length}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {payouts.filter(p => p.status === 'completed').length}
                  </p>
                </div>
              </div>

              {/* Payouts Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Affiliate</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Month</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Requested</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((payout) => (
                      <tr key={payout.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-black">{payout.affiliateName}</p>
                          <p className="text-xs text-gray-500">{payout.affiliateEmail}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{payout.month}</td>
                        <td className="py-3 px-4 text-right font-bold text-black">
                          ₹{(payout?.amount || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(payout.status)}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(payout.requestedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {payout.status === 'pending' ? (
                            <button
                              onClick={() => {
                                setSelectedPayout(payout);
                                setShowPayoutModal(true);
                              }}
                              className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-semibold"
                            >
                              Process
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500">
                              {payout.transactionId}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'commissions' && (
            <div className="space-y-6">
              {/* Commission Summary */}
              {commissionSummary && (
                <div className="grid grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Total Affiliates</p>
                    <p className="text-2xl font-bold text-black">{commissionSummary.totalAffiliates}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Active Earners</p>
                    <p className="text-2xl font-bold text-green-600">{commissionSummary.activeAffiliates}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Monthly Total</p>
                    <p className="text-2xl font-bold text-yellow-600">₹{(commissionSummary?.totalMonthlyCommissions || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Avg/Affiliate</p>
                    <p className="text-2xl font-bold text-purple-600">₹{(commissionSummary?.averageCommissionPerAffiliate || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Report Month</p>
                    <p className="text-lg font-bold text-black">{commissionSummary.currentMonth}</p>
                  </div>
                </div>
              )}

              {/* Commissions List */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-black">Monthly Commissions Breakdown</h3>
                
                {commissions.length > 0 ? (
                  commissions.map((commission) => (
                    <div key={commission.affiliateId} className="bg-white border border-gray-200 rounded-lg p-5">
                      {/* Affiliate Info */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-black text-lg">{commission.affiliateName}</h4>
                          <p className="text-sm text-gray-600">{commission.affiliateEmail}</p>
                          <code className="inline-block mt-1 bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">
                            {commission.referralCode}
                          </code>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Monthly Commission</p>
                          <p className="text-3xl font-bold text-green-600">₹{(commission?.monthlyCommission || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-1">{commission.commissionRate}% of subscriptions</p>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-4 gap-3 mb-4 pb-4 border-b">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Total Referrals</p>
                          <p className="text-lg font-bold text-black">{commission.totalReferrals}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Active (Paying)</p>
                          <p className="text-lg font-bold text-green-600">{commission.activeReferrals}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Lifetime Earnings</p>
                          <p className="text-lg font-bold text-black">₹{(commission?.lifetimeEarnings || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Pending Balance</p>
                          <p className="text-lg font-bold text-yellow-600">₹{(commission?.pendingEarnings || 0).toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Payment Details</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Method: </span>
                            <span className="font-medium text-black">
                              {commission.bankDetails.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 'UPI'}
                            </span>
                          </div>
                          {commission.bankDetails.paymentMethod === 'bank_transfer' ? (
                            <>
                              <div>
                                <span className="text-gray-600">Bank: </span>
                                <span className="text-black">{commission.bankDetails.bankName}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">A/C: </span>
                                <code className="text-black font-mono text-xs">{commission.bankDetails.accountNumber}</code>
                              </div>
                              <div>
                                <span className="text-gray-600">IFSC: </span>
                                <code className="text-black font-mono text-xs">{commission.bankDetails.ifscCode}</code>
                              </div>
                            </>
                          ) : (
                            <div>
                              <span className="text-gray-600">UPI ID: </span>
                              <code className="text-black font-mono text-xs">{commission.bankDetails.upiId}</code>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Referral Details */}
                      {commission.referralDetails && commission.referralDetails.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-2">
                            Referred Employers ({commission.referralDetails.length})
                          </p>
                          <div className="space-y-2">
                            {commission.referralDetails.map((referral, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded border text-sm ${
                                  referral.isActive 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="font-semibold text-black">{referral.companyName}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                        referral.isActive
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {(referral?.subscriptionStatus || 'inactive').toUpperCase()}
                                      </span>
                                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">
                                        {(referral?.subscriptionPlan || 'free').toUpperCase()}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-600">{referral.employerEmail}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      <Calendar size={10} className="inline" /> Joined: {new Date(referral.joinedDate).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-600">Plan: ₹{referral.subscriptionAmount}/mo</p>
                                    <p className={`text-sm font-bold ${referral.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                      Commission: ₹{referral.monthlyCommission}/mo
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign size={48} className="mx-auto mb-3 opacity-50" />
                    <p className="font-semibold">No commission data available</p>
                    <p className="text-sm mt-1">Commissions will appear when affiliates refer paying employers</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Affiliate Detail Modal */}
      {showDetailModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-black">{selectedAffiliate.name}</h3>
                  <p className="text-sm text-gray-600">{selectedAffiliate.email}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Status</p>
                {getStatusBadge(selectedAffiliate.status)}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
                    <Phone size={14} /> Phone
                  </p>
                  <p className="text-sm text-gray-600">{selectedAffiliate.phone}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
                    <Globe size={14} /> Website
                  </p>
                  <a 
                    href={selectedAffiliate.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {selectedAffiliate.website}
                  </a>
                </div>
              </div>

              {/* Referral Code */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Referral Code</p>
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  {selectedAffiliate.referralCode}
                </code>
              </div>

              {/* Earnings */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Total Earnings</p>
                  <p className="text-lg font-bold text-green-600">
                    ₹{(selectedAffiliate?.totalEarnings || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Pending</p>
                  <p className="text-lg font-bold text-yellow-600">
                    ₹{(selectedAffiliate?.pendingEarnings || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Paid</p>
                  <p className="text-lg font-bold text-blue-600">
                    ₹{(selectedAffiliate?.paidEarnings || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Referrals */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Total Referrals</p>
                  <p className="text-lg font-bold text-black">{selectedAffiliate.totalReferrals || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Active</p>
                  <p className="text-lg font-bold text-green-600">{selectedAffiliate.activeReferrals || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Conversion Rate</p>
                  <p className="text-lg font-bold text-black">{selectedAffiliate.conversionRate || 0}%</p>
                </div>
              </div>

              {/* Payment Details */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <CreditCard size={16} /> Payment Information
                </p>
                
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  {/* Payment Method */}
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Payment Method</p>
                    <p className="text-sm font-medium text-black capitalize">
                      {selectedAffiliate.paymentMethod === 'upi' ? 'UPI' : 
                       selectedAffiliate.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 
                       selectedAffiliate.paymentMethod || 'Not specified'}
                    </p>
                  </div>

                  {/* UPI Details */}
                  {selectedAffiliate.paymentMethod === 'upi' && selectedAffiliate.upiId && (
                    <div>
                      <p className="text-xs text-gray-600 mb-1">UPI ID</p>
                      <code className="bg-white px-3 py-2 rounded text-sm font-mono border border-gray-200 block">
                        {selectedAffiliate.upiId}
                      </code>
                    </div>
                  )}

                  {/* Bank Details */}
                  {selectedAffiliate.paymentMethod === 'bank_transfer' && selectedAffiliate.bankDetails && (
                    <div className="space-y-2">
                      {selectedAffiliate.bankDetails.accountName && (
                        <div>
                          <p className="text-xs text-gray-600">Account Name</p>
                          <p className="text-sm font-medium text-black">{selectedAffiliate.bankDetails.accountName}</p>
                        </div>
                      )}
                      {selectedAffiliate.bankDetails.accountNumber && (
                        <div>
                          <p className="text-xs text-gray-600">Account Number</p>
                          <code className="text-sm font-mono text-black">{selectedAffiliate.bankDetails.accountNumber}</code>
                        </div>
                      )}
                      {selectedAffiliate.bankDetails.bankName && (
                        <div>
                          <p className="text-xs text-gray-600">Bank Name</p>
                          <p className="text-sm text-black">{selectedAffiliate.bankDetails.bankName}</p>
                        </div>
                      )}
                      {selectedAffiliate.bankDetails.ifscCode && (
                        <div>
                          <p className="text-xs text-gray-600">IFSC Code</p>
                          <code className="text-sm font-mono text-black">{selectedAffiliate.bankDetails.ifscCode}</code>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No payment details */}
                  {!selectedAffiliate.paymentMethod || 
                   (selectedAffiliate.paymentMethod === 'upi' && !selectedAffiliate.upiId) ||
                   (selectedAffiliate.paymentMethod === 'bank_transfer' && !selectedAffiliate.bankDetails?.accountNumber) ? (
                    <div className="text-center py-2">
                      <p className="text-sm text-gray-500 italic">No payment details provided yet</p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Actions */}
              {selectedAffiliate.status === 'pending' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleApprove(selectedAffiliate.id)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center gap-2 font-semibold"
                  >
                    <Check size={18} />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(selectedAffiliate.id)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center gap-2 font-semibold"
                  >
                    <X size={18} />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payout Processing Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold text-black">Process Payout</h3>
                <button
                  onClick={() => setShowPayoutModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600">Affiliate</p>
                <p className="font-semibold text-black">{selectedPayout.affiliateName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-2xl font-bold text-green-600">₹{(selectedPayout?.amount || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Month</p>
                <p className="font-semibold text-black">{selectedPayout.month}</p>
              </div>

              {/* Payment Details */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <CreditCard size={16} /> Payment Information
                </p>
                {(() => {
                  const affiliate = affiliates.find(a => a.id === selectedPayout.affiliateId);
                  if (!affiliate) {
                    return <p className="text-sm text-gray-500 italic">Payment details not available</p>;
                  }
                  
                  return (
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Method: </span>
                        <span className="font-medium text-black capitalize">
                          {affiliate.paymentMethod === 'upi' ? 'UPI' : 
                           affiliate.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 
                           'Not specified'}
                        </span>
                      </div>
                      
                      {affiliate.paymentMethod === 'upi' && affiliate.upiId && (
                        <div>
                          <span className="text-gray-600">UPI ID: </span>
                          <code className="font-mono text-black bg-white px-2 py-1 rounded border">
                            {affiliate.upiId}
                          </code>
                        </div>
                      )}
                      
                      {affiliate.paymentMethod === 'bank_transfer' && affiliate.bankDetails && (
                        <div className="space-y-1">
                          {affiliate.bankDetails.accountName && (
                            <div>
                              <span className="text-gray-600">Account: </span>
                              <span className="font-medium text-black">{affiliate.bankDetails.accountName}</span>
                            </div>
                          )}
                          {affiliate.bankDetails.accountNumber && (
                            <div>
                              <span className="text-gray-600">A/C No: </span>
                              <code className="font-mono text-black">{affiliate.bankDetails.accountNumber}</code>
                            </div>
                          )}
                          {affiliate.bankDetails.bankName && (
                            <div>
                              <span className="text-gray-600">Bank: </span>
                              <span className="text-black">{affiliate.bankDetails.bankName}</span>
                            </div>
                          )}
                          {affiliate.bankDetails.ifscCode && (
                            <div>
                              <span className="text-gray-600">IFSC: </span>
                              <code className="font-mono text-black">{affiliate.bankDetails.ifscCode}</code>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg flex items-start gap-2">
                <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Confirm Payment</p>
                  <p>You'll be asked to enter a transaction ID to confirm the payment was sent.</p>
                </div>
              </div>

              <button
                onClick={handleProcessPayout}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold"
              >
                Process Payout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal />
    </div>
  );
};

export default AffiliateManagement;
